import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, Check, X, Trash2, Clock, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/utils/constants';

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    rejected: 'bg-rose-100 text-rose-700 border-rose-200',
  };
  const labels = { pending: 'En attente', approved: 'Approuvé', rejected: 'Rejeté' };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full border ${styles[status] || styles.pending}`} data-testid={`review-status-${status}`}>
      {labels[status] || status}
    </span>
  );
};

const Stars = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={14}
        className={value >= n ? 'fill-amber-400 text-amber-400' : 'text-stone-300'}
      />
    ))}
  </div>
);

const AdminReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all' ? `${API}/admin/reviews` : `${API}/admin/reviews?status=${filter}`;
      const res = await axios.get(url);
      setReviews(res.data || []);
    } catch (err) {
      toast.error('Erreur lors du chargement des avis');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}`, { status });
      toast.success(status === 'approved' ? 'Avis approuvé' : 'Avis rejeté');
      load();
    } catch (err) {
      toast.error('Erreur lors de la modération');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer définitivement cet avis ?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`);
      toast.success('Avis supprimé');
      load();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filters = [
    { key: 'pending', label: 'En attente', icon: Clock },
    { key: 'approved', label: 'Approuvés', icon: Check },
    { key: 'rejected', label: 'Rejetés', icon: X },
    { key: 'all', label: 'Tous', icon: null },
  ];

  return (
    <Card className="bg-white/70 backdrop-blur border-stone-200" data-testid="admin-reviews-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg font-semibold text-stone-800">
            Avis ({reviews.length})
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            data-testid="reviews-refresh-btn"
          >
            <RefreshCw size={16} className="mr-1" />
            Actualiser
          </Button>
        </div>
        <div className="flex gap-2 flex-wrap mt-3">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                filter === f.key
                  ? 'bg-indigo-500 text-white'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
              data-testid={`reviews-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <p className="text-center text-stone-400 py-6">Chargement...</p>
        ) : reviews.length === 0 ? (
          <p className="text-center text-stone-400 py-6" data-testid="reviews-empty">
            Aucun avis dans cette catégorie
          </p>
        ) : (
          <div className="space-y-3">
            {reviews.map((r) => (
              <div
                key={r.id}
                className="p-4 bg-stone-50 rounded-lg border border-stone-200"
                data-testid={`review-item-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-stone-800">{r.name}</p>
                      <StatusBadge status={r.status} />
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Stars value={r.rating} />
                      <span className="text-xs text-stone-400">
                        {new Date(r.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>
                <h4 className="font-medium text-stone-800 mb-1">{r.title}</h4>
                <p className="text-sm text-stone-600 mb-3 whitespace-pre-wrap break-words">{r.comment}</p>
                <div className="flex gap-2 flex-wrap">
                  {r.status !== 'approved' && (
                    <Button
                      size="sm"
                      onClick={() => moderate(r.id, 'approved')}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid={`review-approve-${r.id}`}
                    >
                      <Check size={14} className="mr-1" />
                      Approuver
                    </Button>
                  )}
                  {r.status !== 'rejected' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => moderate(r.id, 'rejected')}
                      className="border-rose-300 text-rose-600 hover:bg-rose-50"
                      data-testid={`review-reject-${r.id}`}
                    >
                      <X size={14} className="mr-1" />
                      Rejeter
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(r.id)}
                    className="text-stone-500 hover:text-rose-600 hover:bg-rose-50"
                    data-testid={`review-delete-${r.id}`}
                  >
                    <Trash2 size={14} className="mr-1" />
                    Supprimer
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminReviewsTab;
